/**
 * Chip Component - Constants
 * ===========================
 * Tipos y mapas de estilo del chip (Figma: node 620:339). Los colores salen
 * de los tokens ya migrados del sistema Figma (sin tokens nuevos) — mismo
 * patrón `intent x emphasis` que Button/Fab. `emphasis="neutral"` (theme
 * "grey" en Figma) ignora el intent: mismo fondo/texto neutro para
 * cualquier color, tal como en el archivo de diseño.
 */

import { getIcon } from '../../icons';

// ============================================================================
// TYPES
// ============================================================================

export type ChipIntent = 'prime' | 'prime-alt' | 'success' | 'primary' | 'danger';

export type ChipEmphasis = 'filled' | 'soft' | 'neutral';
export type ChipSize = 'sm' | 'md' | 'lg';
export type ChipIconType = 'none' | 'prime' | 'check' | 'star' | 'shield-check';

export interface ChipSizeStyles {
  container: string;
  gap: string;
  icon: string;
  label: string;
}

export interface ChipDefaultValues {
  intent: ChipIntent;
  emphasis: ChipEmphasis;
  size: ChipSize;
  icon: ChipIconType;
}

// ============================================================================
// INTENT × EMPHASIS
// ============================================================================
// "neutral" (theme "grey" en Figma) vive aparte en BASE_CLASSES.neutral: no
// depende del intent, a diferencia de filled/soft.

export const INTENT_EMPHASIS: Record<ChipIntent, Record<'filled' | 'soft', string>> = {
  prime: {
    filled: 'ec-bg-prime ec-text-white',
    soft: 'ec-bg-prime/12 ec-text-prime'
  },
  'prime-alt': {
    filled: 'ec-bg-prime-alt ec-text-white',
    soft: 'ec-bg-prime-alt/12 ec-text-prime-alt'
  },
  success: {
    filled: 'ec-bg-success ec-text-white',
    soft: 'ec-bg-success/12 ec-text-success'
  },
  primary: {
    filled: 'ec-bg-color-primary ec-text-color-primary-contrast',
    soft: 'ec-bg-color-primary-light ec-text-color-primary-dark'
  },
  danger: {
    filled: 'ec-bg-danger ec-text-white',
    soft: 'ec-bg-danger/12 ec-text-danger'
  }
};

// ============================================================================
// SIZES / BASE
// ============================================================================

export const SIZES: Record<ChipSize, ChipSizeStyles> = {
  sm: { container: 'ec-px-1 ec-py-1', gap: 'ec-gap-1', icon: 'ec-w-3 ec-h-3', label: 'ec-text-[11px] ec-tracking-[-0.33px]' },
  md: { container: 'ec-px-2 ec-py-1', gap: 'ec-gap-1', icon: 'ec-w-4 ec-h-4', label: 'ec-text-[13px] ec-tracking-[-0.39px]' },
  lg: { container: 'ec-p-2', gap: 'ec-gap-1', icon: 'ec-w-4 ec-h-4', label: 'ec-text-[14px] ec-tracking-[-0.42px]' }
};

export const BASE_CLASSES = {
  chip: 'ec-inline-flex ec-items-center ec-justify-center ec-rounded-lg ec-font-medium ec-font-main ec-leading-none ec-whitespace-nowrap',
  neutral: 'ec-bg-gray-extralight ec-text-gray-dark',
  dismiss:
    'ec-inline-flex ec-items-center ec-justify-center ec-flex-shrink-0 ec-rounded-full ec-border-0 ec-bg-transparent ' +
    'ec-p-0 ec-text-current ec-cursor-pointer hover:ec-opacity-70 focus-visible:ec-outline-none focus-visible:ec-ring-1 focus-visible:ec-ring-current'
};

// ============================================================================
// ICONS
// ============================================================================

/**
 * Ícono izquierdo opcional (`show-icon`). Registro central (`src/icons`),
 * variante `solid` — coincide con la capa "icon/solid/x" de Figma.
 */
export const ICONS: Record<ChipIconType, string> = {
  none: '',
  prime: getIcon('prime', 'solid') || '',
  check: getIcon('check') || '',
  star: getIcon('star') || '',
  'shield-check': getIcon('shield-check', 'solid') || ''
};

/** Ícono del botón de cierre (`dismissible`) — fijo, no configurable. */
export const DISMISS_ICON: string = getIcon('dismiss', 'solid') || '';

// ============================================================================
// DEFAULTS
// ============================================================================

export const DEFAULTS: ChipDefaultValues = {
  intent: 'success',
  emphasis: 'soft',
  size: 'md',
  icon: 'prime'
};
